import React, { useEffect } from 'react'

import { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { StatusUpgrade } from '@/core/api/entities/account/statusUpgrade'
import { Subscription } from '@/core/api/entities/account/subscription'
import { usePartnerCustomization } from '@/core/hooks/misc/usePartnerCustomization'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { isPartnerBuild } from '@/core/utils/buildType'
import { ZoneRequestDTO } from '@/features/zones/Create/Create'

interface ConfirmationStepProps {
	form: UseFormReturn<ZoneRequestDTO>
	currentSubscription: Subscription | null
	accountUpgradeStatus: StatusUpgrade | undefined
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
	form,
	currentSubscription,
	accountUpgradeStatus,
}) => {
	const { t } = useTranslation('common')
	const { partnerName } = usePartnerCustomization()
	const { resellerSettings } = usePermission()
	const { setValue } = form

	const isReseller = !!resellerSettings
	const showPrices = !isReseller

	// Once the component is mounted, the acknowledgement is automatic
	useEffect(() => {
		setValue('acknowledgement', true)
	}, [setValue])

	// Get zone creation summary from Redux
	const summary = useSelector(
		(state: RootState) => state.zones.createZone.summary
	)
	const { zonePrice, boxPrice, soundreefPrice, newTotalSoundreef, total } =
		summary

	// Check if we need to display Box section,based on the device_type selected in the form
	const showBox = form.watch('device_type') === 'box'

	// Check if we need to display Soundreef section
	const showSoundreef = !!summary.newSoundreef

	// Check if we're in partner mode
	const isPartnerMode = isPartnerBuild()

	// Get billing cycle text
	const billingCycleShort = !isPartnerMode
		? t(
				'entities.billing.cycles.billed_short.' +
					currentSubscription?.periodCycle
			)
		: ''
	const billingCycle = !isPartnerMode
		? t('entities.billing.cycles.billed.' + currentSubscription?.periodCycle)
		: ''

	// Get currency
	const currency = currentSubscription?.currency || 'USD'

	// Format price with currency and billing cycle
	const formatPrice = (price: number | null) => {
		if (price === null) return ''

		return `${price.toFixed(2)} ${currency}`
	}

	// Render price with currency and billing cycle, optionally with a custom className
	const renderPrice = (
		price: number | null,
		className: string = 'text-base text-foreground flex items-end gap-1'
	) => {
		return (
			<span className={className}>
				<span className="font-semibold">{formatPrice(price)}</span>
				<span className="text-sm text-muted-foreground font-dm-sans">
					/{billingCycleShort}
				</span>
			</span>
		)
	}

	return (
		<div className=" max-w-[700px] bg-shade rounded-xl border lg:min-h-[50vh] flex flex-col">
			{/* Section 1: New items being added */}
			<div className="space-y-3 p-6 flex-1 bg-background/40">
				<p className="text-sm text-muted-foreground">
					{t('entities.zone.create.step_5.about_to_add')}
				</p>

				{/* Line items */}
				<div className="space-y-3">
					{/* Zone */}
					<div className="flex justify-between items-center">
						<span className="text-base text-foreground">
							{summary.newZones} {t('entities.zone.create.step_5.zone')}
						</span>
						{showPrices && renderPrice(zonePrice)}
					</div>

					{/* Box (conditional) */}
					{showBox && (
						<div className="flex justify-between items-center">
							<span className="text-base text-foreground">
								{summary.newBoxes}{' '}
								{t('entities.zone.create.step_5.box', {
									company: partnerName,
								})}
							</span>
							{showPrices && renderPrice(boxPrice)}
						</div>
					)}

					{/* Soundreef (conditional) */}
					{showSoundreef && (
						<div className="flex justify-between items-center">
							<span className="text-base text-foreground">
								{summary.newSoundreef}{' '}
								{t('entities.zone.create.step_5.soundreef', {
									company: partnerName,
								})}
							</span>
							{showPrices && renderPrice(soundreefPrice)}
						</div>
					)}
				</div>

				{/* Description text */}
				{showPrices && !isPartnerMode && billingCycle && (
					<p className="text-sm text-muted-foreground pt-2">
						{accountUpgradeStatus?.willUpdateSubscription
							? t('entities.zone.create.step_5.billing_description', {
									cycle: billingCycle.toLowerCase(),
								})
							: t('entities.zone.create.step_5.billing_description_no_change', {
									cycle: billingCycle.toLowerCase(),
								})}
					</p>
				)}

				{isReseller && accountUpgradeStatus?.willUpdateSubscription && (
					<p className="text-sm text-muted-foreground pt-2">
						{t('entities.zone.create.step_5.reseller_billing_description')}
					</p>
				)}
			</div>

			{/* Section 2: Total subscription after confirming */}
			{showPrices && (
				<div className="space-y-3 p-6 border-t border-border">
					<p className="text-sm text-muted-foreground">
						{t('entities.zone.create.step_5.total_summary_title', {
							company: partnerName,
						})}
					</p>

					<div className="flex lg:flex-row flex-col justify-between lg:items-center">
						<span className="text-2xl text-foreground font-semibold">
							{t('entities.zone.create.step_5.total')}
						</span>
						{renderPrice(
							total,
							'text-2xl text-foreground flex items-end gap-1'
						)}
					</div>

					{/* Soundreef total (if applicable) */}
					{showSoundreef && (
						<div className="flex justify-between items-center pt-2 border-t border-border/50">
							<span className="text-sm text-muted-foreground">
								{t('entities.zone.create.step_5.total_summary_soundreef_title')}{' '}
								({summary.newTotalSoundreef}{' '}
								{t('entities.zone.create.step_5.soundreef')})
							</span>
							{renderPrice(newTotalSoundreef * soundreefPrice)}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

export default ConfirmationStep
